import styled, { keyframes, css } from 'styled-components';

import { UnifiedTheme } from './index';

// Animations
export const typeEffect = keyframes`
  from { width: 0 }
  to { width: 100% }
`;

export const blink = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
`;

export const pulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 0 rgba(31, 207, 94, 0.4);
  }
  50% { 
    box-shadow: 0 0 10px rgba(31, 207, 94, 0.7);
  }
`;

export const matrixRain = keyframes`
  0% { transform: translateY(-100vh); }
  100% { transform: translateY(100vh); }
`;

// Terminal/Container Components
export const TerminalContainer = styled.div<{ theme: UnifiedTheme }>`
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
`;

export const TerminalWindow = styled.div<{ $transparent?: boolean; theme: UnifiedTheme }>`
  background: ${({ theme, $transparent }) =>
    $transparent ? theme.colors.backgroundLighter : theme.colors.backgroundSemi};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.lg} 0;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

export const TerminalHeader = styled.div<{ theme: UnifiedTheme }>`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Prompt = styled.div<{ theme: UnifiedTheme }>`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};

  .user {
    color: ${({ theme }) => theme.colors.secondary};
  }

  .at {
    color: ${({ theme }) => theme.colors.text};
  }

  .host {
    color: ${({ theme }) => theme.colors.accent};
  }

  .path {
    color: ${({ theme }) => theme.colors.warning};
  }

  .dollar {
    color: ${({ theme }) => theme.colors.text};
    margin: 0 ${({ theme }) => theme.spacing.xs};
  }

  svg {
    margin-right: ${({ theme }) => theme.spacing.xs};
    font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  }
`;

// Typography Components
export const HackerTitle = styled.h2<{
  $size?: 'large' | 'medium' | 'small';
  theme: UnifiedTheme;
}>`
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.lg} 0;
  text-shadow: ${({ theme }) => theme.shadows.hacker};

  ${({ $size, theme }) => {
    switch ($size) {
      case 'large':
        return css`
          font-size: ${theme.typography.fontSizes['3xl']};
          @media (max-width: ${theme.breakpoints.mobile}) {
            font-size: ${theme.typography.fontSizes['2xl']};
          }
        `;
      case 'small':
        return css`
          font-size: ${theme.typography.fontSizes.xl};
          @media (max-width: ${theme.breakpoints.mobile}) {
            font-size: ${theme.typography.fontSizes.lg};
          }
        `;
      default:
        return css`
          font-size: ${theme.typography.fontSizes['2xl']};
          @media (max-width: ${theme.breakpoints.mobile}) {
            font-size: ${theme.typography.fontSizes.xl};
          }
        `;
    }
  }}
`;

export const HackerSubtitle = styled.h3<{ theme: UnifiedTheme }>`
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  color: ${({ theme }) => theme.colors.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  font-weight: ${({ theme }) => theme.typography.fontWeights.normal};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  }
`;

export const HackerText = styled.p<{ $muted?: boolean; theme: UnifiedTheme }>`
  font-family: ${({ theme }) => theme.typography.fonts.sans};
  color: ${({ theme, $muted }) => ($muted ? theme.colors.textMuted : theme.colors.text)};
  line-height: ${({ theme }) => theme.typography.lineHeights.relaxed};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  font-weight: ${({ theme }) => theme.typography.fontWeights.normal};
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

// Button Components
export const HackerButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger';
  $size?: 'small' | 'medium' | 'large';
  theme: UnifiedTheme;
}>`
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  border: 2px solid;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${({ theme, $variant }) => {
    const variant = $variant || 'primary';
    switch (variant) {
      case 'secondary':
        return css`
          background: transparent;
          color: ${theme.colors.textMuted};
          border-color: ${theme.colors.border};

          &:hover {
            background: ${theme.colors.backgroundLight};
            border-color: ${theme.colors.borderBright};
            color: ${theme.colors.text};
          }
        `;
      case 'danger':
        return css`
          background: transparent;
          color: ${theme.colors.error};
          border-color: ${theme.colors.error};

          &:hover {
            background: rgba(255, 0, 0, 0.1);
            color: white;
          }
        `;
      default:
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.background};
          border-color: ${theme.colors.primary};

          &:hover {
            background: transparent;
            color: ${theme.colors.primary};
            box-shadow: ${theme.shadows.hacker};
          }
        `;
    }
  }}

  ${({ theme, $size }) => {
    const size = $size || 'medium';
    switch (size) {
      case 'small':
        return css`
          padding: ${theme.spacing.xs} ${theme.spacing.md};
          font-size: ${theme.typography.fontSizes.xs};
        `;
      case 'large':
        return css`
          padding: ${theme.spacing.md} ${theme.spacing.xl};
          font-size: ${theme.typography.fontSizes.md};
        `;
      default:
        return css`
          padding: ${theme.spacing.sm} ${theme.spacing.lg};
          font-size: ${theme.typography.fontSizes.sm};
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// List Components
export const HackerList = styled.div<{ theme: UnifiedTheme }>`
  margin: ${({ theme }) => theme.spacing.lg} 0;
`;

export const HackerListItem = styled.div<{ $active?: boolean; theme: UnifiedTheme }>`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  margin: ${({ theme }) => theme.spacing.xs} 0;
  background: ${({ theme, $active }) => ($active ? theme.colors.backgroundLight : 'transparent')};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.borderBright : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.textMuted)};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundLight};
    border-color: ${({ theme }) => theme.colors.borderBright};
    color: ${({ theme }) => theme.colors.text};
  }

  .icon {
    margin-right: ${({ theme }) => theme.spacing.sm};
    color: ${({ theme }) => theme.colors.primary};
  }

  .prefix {
    color: ${({ theme }) => theme.colors.primary};
    margin-right: ${({ theme }) => theme.spacing.xs};
    opacity: 0.8;
  }
`;

// Grid Components
export const HackerGrid = styled.div<{ $columns?: number; theme: UnifiedTheme }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin: ${({ theme }) => theme.spacing.lg} 0;

  ${({ $columns }) =>
    $columns &&
    css`
      grid-template-columns: repeat(${$columns}, 1fr);

      @media (max-width: 900px) {
        grid-template-columns: ${$columns > 2 ? 'repeat(2, 1fr)' : '1fr'} !important;
      }

      @media (max-width: 600px) {
        grid-template-columns: 1fr !important;
      }
    `}

  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr !important;
  }
`;

export const HackerCard = styled.div<{
  $highlight?: boolean;
  $clickable?: boolean;
  theme: UnifiedTheme;
}>`
  background: ${({ theme }) => theme.colors.backgroundLighter};
  border: 1px solid
    ${({ theme, $highlight }) => ($highlight ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  position: relative;

  ${({ $highlight, theme }) =>
    $highlight &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: ${theme.colors.primary};
      }
    `}

  ${({ $clickable, theme }) =>
    $clickable &&
    css`
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        border-color: ${theme.colors.borderBright};
        transform: translateY(-2px);
        box-shadow: ${theme.shadows.hacker};
      }
    `}
`;

// Input Components
export const HackerInput = styled.input<{ theme: UnifiedTheme }>`
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  background: ${({ theme }) => theme.colors.inputBg};
  border: 1px solid ${({ theme }) => theme.colors.inputBorder};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  width: 100%;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadows.hacker};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textDark};
  }
`;

